import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import api from "../api";

export default function AddressField({ label, value, onSelect, testId, placeholder }) {
  const [query, setQuery] = useState(value?.address || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => { setQuery(value?.address || ""); }, [value?.address]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const search = (v) => {
    setQuery(v);
    clearTimeout(timer.current);
    if (v.trim().length < 3) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/maps/geocode", { params: { q: v } });
        setResults(data.results || []);
        setOpen(true);
      } catch { setResults([]); }
      setLoading(false);
    }, 280);
  };

  const choose = (r) => {
    setQuery(r.label);
    setOpen(false);
    setResults([]);
    onSelect({ address: r.label, lat: r.lat, lng: r.lng, city: r.city, postcode: r.postcode, country: r.country });
  };

  return (
    <div className="relative" ref={boxRef}>
      {label && <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">{label}</label>}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <input
          data-testid={testId}
          value={query}
          onChange={(e) => search(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder || "Address, postcode or city"}
          className="w-full border border-zinc-300 focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/20 rounded-lg text-sm bg-white pl-9 pr-8 py-2.5 outline-none transition-shadow"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-zinc-400" />}
      </div>
      {open && results.length > 0 && (
        <div data-testid={`${testId}-results`} className="absolute z-30 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg overflow-hidden">
          {results.map((r) => (
            <button key={r.id} type="button" onClick={() => choose(r)}
              data-testid={`${testId}-option`}
              className="block w-full text-left px-3.5 py-2.5 text-sm hover:bg-zinc-100 transition-colors border-b border-zinc-100 last:border-0">
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
